import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { CommandInput } from "@v1/components/designSystem/shadcn/core/command";
import { Search } from "lucide-react";

import { useCombobox } from "../hooks/useCombobox";
import { OptionsList } from "./OptionsList";

export type ComboboxContentProps = {
  style?: React.CSSProperties;
  size?: "sm" | "base" | "lg";
  title?: string | React.ReactNode;
  areOptionsSearchable?: boolean;
  inputPlaceholder?: string;
  emptySearchMessage?: string;
  footer?: React.ReactNode;
  onScrolledToEnd?: () => void;
  loader?: React.ReactNode | null;
  UNSAFE_listClassName?: string;
};

export const ComboboxContent: React.FC<
  React.PropsWithChildren<ComboboxContentProps>
> = ({
  title,
  areOptionsSearchable,
  inputPlaceholder,
  emptySearchMessage,
  footer,
  size = "sm",
  onScrolledToEnd,
  loader,
  UNSAFE_listClassName,
  children,
}) => {
  const { options, setValue, setOpen, value, input, setInput } = useCombobox();

  return (
    <>
      <div
        className={twMerge(
          "flex flex-col gap-2 px-2",
          (areOptionsSearchable || children) && "border-border border-b py-2",
          !areOptionsSearchable && title && "pt-2",
        )}
      >
        {title && (
          <div
            className={twMerge(
              typeof title === "string" &&
                (size === "sm" ? "text-xs font-semibold" : "header-h6"),
            )}
          >
            {title}
          </div>
        )}
        {areOptionsSearchable && (
          <CommandInput
            value={input}
            onValueChange={setInput}
            placeholder={inputPlaceholder ?? "Search..."}
            startEnhancer={
              <Search
                className={twMerge(
                  "text-muted",
                  size === "sm" ? "size-2.5" : "size-3",
                )}
              />
            }
            variant={size === "lg" ? "base" : size}
          />
        )}
        {children}
      </div>
      <OptionsList
        options={options}
        setValue={setValue}
        setOpen={setOpen}
        value={value}
        input={input}
        emptySearchMessage={emptySearchMessage}
        setInput={setInput}
        areOptionsSearchable={areOptionsSearchable}
        size={size}
        onScrolledToEnd={onScrolledToEnd}
        loader={loader}
        UNSAFE_className={UNSAFE_listClassName}
      />
      {footer}
    </>
  );
};
